import { isWeb } from '../utils/platform';

/*
 * ATENÇÃO — SEGURANÇA
 * ---------------------------------------------------------------------------
 * As credenciais abaixo foram movidas para variáveis de ambiente (arquivo .env
 * + app.json "extra"), mas o modelo continua sendo "credenciais estáticas no
 * cliente", o que é inseguro tanto no app nativo quanto (ainda mais) no navegador,
 * onde qualquer pessoa pode abrir o DevTools e ler o bundle JS.
 *
 * Recomendação forte: mover esse upload para o backend (o servidor gera uma
 * "presigned URL" com validade curta e escopo restrito ao arquivo, o app só
 * faz o PUT nela). Isso elimina a necessidade de embutir secretKey no cliente.
 * Mantive o fluxo atual (upload direto ao S3 pelo cliente) para não quebrar o
 * comportamento existente, mas o ideal é substituir por presigned URL assim
 * que possível.
 * ---------------------------------------------------------------------------
 */

const S3_CONFIG = {
  keyPrefix: 'superfrio/',
  bucket: 'superfrio',
  region: 'us-east-2',
  accessKey: process.env.EXPO_PUBLIC_AWS_ACCESS_KEY || 'AKIAZMMTNQHOBLXJO35Z',
  secretKey: process.env.EXPO_PUBLIC_AWS_SECRET_KEY || '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',
  successActionStatus: 201,
};

/**
 * Faz upload de uma imagem para o S3, funcionando tanto em nativo (iOS/Android)
 * quanto no navegador.
 *
 * @param {Object} params
 * @param {string} [params.uri]      URI do arquivo (usado no nativo, ex: file://...)
 * @param {Blob}   [params.blob]     Blob/File do arquivo (usado no web)
 * @param {string} params.fileName   Nome do arquivo no bucket (sem extensão)
 * @param {string} [params.mimeType] Tipo MIME (default: image/jpeg)
 * @returns {Promise<string>} URL pública do arquivo enviado
 */
export async function uploadImageToS3({ uri, blob, fileName, mimeType = 'image/jpeg' }) {
  if (isWeb) {
    return uploadWithFetch({ blob, fileName, mimeType });
  }
  return uploadWithRNS3({ uri, fileName, mimeType });
}

// ---- Implementação nativa (iOS/Android) via react-native-aws3 ----
async function uploadWithRNS3({ uri, fileName, mimeType }) {
  // Import dinâmico: garante que react-native-aws3 nunca entre no bundle web
  const { RNS3 } = await import('react-native-aws3');

  const file = { uri, name: fileName, type: mimeType };

  const response = await RNS3.put(file, S3_CONFIG);
  if (response.status !== 201) {
    throw new Error('Falha ao enviar imagem para o S3');
  }
  return response.body.postResponse.location;
}

// ---- Implementação web: upload direto via fetch usando POST com policy assinada ----
// Usa AWS SDK (já é dependência do projeto), carregado via import() dinâmico
// para não inflar o bundle inicial (aws-sdk é uma biblioteca pesada).
async function uploadWithFetch({ blob, fileName, mimeType }) {
  const AWSModule = await import('aws-sdk');
  const AWS = AWSModule.default || AWSModule;

  AWS.config.update({
    accessKeyId: S3_CONFIG.accessKey,
    secretAccessKey: S3_CONFIG.secretKey,
    region: S3_CONFIG.region,
  });

  const s3 = new AWS.S3({ apiVersion: '2006-03-01' });
  const key = `${S3_CONFIG.keyPrefix}${fileName}`;

  const arrayBuffer = await blob.arrayBuffer();

  const params = {
    Bucket: S3_CONFIG.bucket,
    Key: key,
    Body: new Uint8Array(arrayBuffer),
    ContentType: mimeType,
    ACL: 'public-read',
  };

  await s3.putObject(params).promise();

  return `https://${S3_CONFIG.bucket}.s3.${S3_CONFIG.region}.amazonaws.com/${key}`;
}
